import {ServiceProviderRepository} from './ServiceProviderRepository';
import {ServiceProvider, ServiceProviderWithBrands} from '../models/serviceProvider.model';
import {ServiceProviderAPIDataSource} from '../api/ServiceProviderAPIDataSource';
import {mapServiceProviderDTOToModel, mapServiceProviderWithBrandsDTOToModel, mapServiceProviderModelToDTO} from '../mappers/serviceProvider.mapper';
import type {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import type {MyBrands, ProviderBrand} from '../../../types/ServiceProviderTypes';

export class ServiceProviderRepositoryImpl implements ServiceProviderRepository {
  async getAll (page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<{ items: ServiceProvider[]; pagination?: Pagination }> {
    const dtos = await ServiceProviderAPIDataSource.getAll(page, limit);
    return {items: dtos.items.map(mapServiceProviderDTOToModel)};
  }

  async getById (id: string): Promise<ServiceProviderWithBrands> {
    const dto = await ServiceProviderAPIDataSource.getById(id);
    return mapServiceProviderWithBrandsDTOToModel(dto);
  }

  async getStats (id: string): Promise<any> {
    return await ServiceProviderAPIDataSource.getStats(id);
  }

  async create (providerData: Omit<ServiceProvider, 'id' | 'status' | 'createdAt' | 'updatedAt'> & { password: string }): Promise<ServiceProvider> {
    const dto = await ServiceProviderAPIDataSource.create({
      username: providerData.username,
      full_name: providerData.fullName,
      email: providerData.email,
      phone_number: providerData.phoneNumber,
      company_name: providerData.companyName,
      website: providerData.website,
      description: providerData.description,
      password: providerData.password
    });
    return mapServiceProviderDTOToModel(dto);
  }

  async update (id: string, providerData: Partial<ServiceProvider>): Promise<ServiceProvider> {
    // Convert partial ServiceProvider model (camelCase) to UpdateProviderRequestDTO (snake_case)
    const updateDTO: any = {};

    if (providerData.fullName !== undefined) {updateDTO.full_name = providerData.fullName;}
    if (providerData.email !== undefined) {updateDTO.email = providerData.email;}
    if (providerData.phoneNumber !== undefined) {updateDTO.phone_number = providerData.phoneNumber;}
    if (providerData.companyName !== undefined) {updateDTO.company_name = providerData.companyName;}
    if (providerData.website !== undefined) {updateDTO.website = providerData.website;}
    if (providerData.description !== undefined) {updateDTO.description = providerData.description;}
    if (providerData.status !== undefined) {updateDTO.status = providerData.status;}

    const dto = await ServiceProviderAPIDataSource.update(id, updateDTO);
    return mapServiceProviderDTOToModel(dto);
  }

  async delete (id: string): Promise<void> {
    await ServiceProviderAPIDataSource.delete(id);
  }

  async assignBrand (providerId: number, brandId: number): Promise<void> {
    await ServiceProviderAPIDataSource.assignBrand({provider_id: providerId, brand_id: brandId});
  }

  async unassignBrand (providerId: number, brandId: number): Promise<any> {
    return await ServiceProviderAPIDataSource.unassignBrand({provider_id: providerId, brand_id: brandId});
  }

  async getProviderBrands (): Promise<any[]> {
    return await ServiceProviderAPIDataSource.getProviderBrands();
  }

  async getMyBrands (): Promise<MyBrands | ProviderBrand[] | undefined> {
    return await ServiceProviderAPIDataSource.getMyBrands();
  }

  async getBrandStores (brandId: string): Promise<any[]> {
    return await ServiceProviderAPIDataSource.getBrandStores(brandId);
  }
}
